import { ReactElement } from 'react';
import { mainColors } from '../../../../../styling/theme';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { Status } from '../../../../../types/redux/data/dataTypes';
import { Fund } from '../../../../../types/server-data/ClientConfig';
import {
  extractProspectusRestrictionsPayload,
  type ProspectusRestrictionsPageModel,
  type ProspectusRestrictionsPayload,
  type ProspectusRestrictionsTimeSeries,
} from '../../../../../api/rs-hg/risk/prospectusRestrictions.types';
import {
  percentageToTwoDecimalPlacesNoHundred,
  toTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import InTableTimeSeries from '../../../../charts/InTableTimeSeries';
import NoTimeSeries from '../../../../charts/NoTimeSeries';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import SecondLevelTable from './second-level-table/second-level-table.component';
import RaptorStatusBox from '../../../../feedback/RaptorStatusBox.component';
import { specifyStatus } from '../../../../../utilities/generalMappings';
import NotesComp from '../../../sharedComponents/notes/Notes';

export interface IProspectusRestrictionsFirstLevelData {
  number: number;
  name: string;
  status: Status;
  value: number | string;
  limit: number | string;
  timeSeries: ProspectusRestrictionsTimeSeries | null;
}

export const csvProspectusRestrictionsFirstLevel = [
  'number',
  'name',
  'status',
  'value',
  'limit',
];

export const firstLevelColumns = (
  section: string,
  fundId: string,
  fundName: string,
  positionDate: string,
): CustomColumn<IProspectusRestrictionsFirstLevelData>[] => [
    { title: '#', field: 'number', width: '0.62rem' },
    { title: 'Name', field: 'name' },
    {
      title: 'Value',
      field: 'value',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: { textAlign: 'center' },
    },

    {
      title: 'Limit',
      field: 'limit',

      cellStyle: { textAlign: 'center' },
    },
    {
      title: 'Status',
      field: 'status',
      render: (rowData: IProspectusRestrictionsFirstLevelData) => (
        <RaptorStatusBox status={specifyStatus(rowData.status)} />
      ),
      width: '8%',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: `Time Series`,
      field: 'time_series',

      render: (rowData: IProspectusRestrictionsFirstLevelData) => {
        if (!rowData.timeSeries) return <NoTimeSeries />;
        const chart = buildTimeSeriesChart(rowData.timeSeries);
        return (
          <InTableTimeSeries
            id={fundId + rowData.number}
            export
            lines={[
              { dataKey: 'limit', color: mainColors.Fail_darker },
              { dataKey: 'value', color: mainColors.mainBlue },
            ]}
            error={null}
            title=""
            data={chart.data}
            yAxisTicks={chart.yAxisTicks}
            yAxisTickFormatter={chart.yAxisTickFormatter}
            yAxisDomain={chart.yAxisDomain}
            formatter={chart.tooltipFormatter}
          />
        );
      },
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        padding: '0.62rem 0',
        textAlign: 'center',
        width: '50%',
      },
    },
    {
      title: 'Notes',
      field: 'notes',
      width: '3.12rem',
      render: (rowData: IProspectusRestrictionsFirstLevelData) => (
        <NotesComp
          positionDate={positionDate}
          fundId={fundId}
          fundName={`${fundName}`}
          subject={`${section}_prospectus-restrictions`}
          topic={`rule_${rowData.number}`}
        />
      ),
      cellStyle: {
        padding: '1.00rem',
        textAlign: 'center',
        paddingRight: '1.50rem',
        paddingLeft: '0.50rem',
      },
      headerStyle: {
        textAlign: 'center',
        padding: 'auto 1.00rem',
        paddingRight: '1.50rem',
        paddingLeft: '0.50rem',
      },
    },
  ];


function parseNumeric(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = parseFloat(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function cell(cell: unknown, key: 'value' | 'limit') {
  if (typeof cell === 'number') return { n: cell, d: null as string | null };
  if (typeof cell === 'string') {
    return { n: parseNumeric(cell), d: cell };
  }
  if (cell && typeof cell === 'object') {
    const o = cell as Record<string, unknown>;
    const raw = o[key];
    return {
      n: parseNumeric(raw),
      d:
        typeof o.display === 'string'
          ? o.display
          : typeof raw === 'string'
            ? raw
            : null,
    };
  }
  return { n: null, d: null };
}

function buildTimeSeriesChart(ts: ProspectusRestrictionsTimeSeries) {
  const data = ts.slice(1).map(([date, value, limit]) => {
    const v = cell(value, 'value');
    const l = cell(limit, 'limit');
    return { date, value: v.n, limit: l.n, valueDisplay: v.d, limitDisplay: l.d };
  });

  const labels = new Map<number, string>();
  data.forEach((r) => {
    if (r.value != null && r.valueDisplay) labels.set(r.value, r.valueDisplay);
    if (r.limit != null && r.limitDisplay) labels.set(r.limit, r.limitDisplay);
  });

  const ticks = [...labels.keys()].sort((a, b) => a - b);
  const fmt = (v: number) =>
    labels.get(v) ?? toTwoDecimalPlaces(v);
  const tooltipFormatter = (v: number, name: string, { payload }: any) =>
    (name === 'value' ? payload?.valueDisplay : payload?.limitDisplay) ??
    percentageToTwoDecimalPlacesNoHundred(v);

  if (!ticks.length) {
    return {
      data,
      tooltipFormatter,
      yAxisTickFormatter: fmt,
      yAxisDomain: [
        (min: number) => (min < 0 ? min * 1.1 : min > 0 ? min * 0.1 : 0),
        (max: number) => Math.round(max * 1.1 * 100) / 100,
      ],
    };
  }

  const pad = (ticks[ticks.length - 1] - ticks[0]) * 0.15 || 1;
  return {
    data,
    tooltipFormatter,
    yAxisTicks: ticks,
    yAxisTickFormatter: fmt,
    yAxisDomain: [ticks[0] - pad, ticks[ticks.length - 1] + pad] as [number, number],
  };
}

export function useBuildFirstLevelProspectusRestrictionsData(
  payload: ProspectusRestrictionsPayload | undefined,
): IProspectusRestrictionsFirstLevelData[] {
  if (!payload?.limit_status?.length) {
    return [];
  }

  const returnData: IProspectusRestrictionsFirstLevelData[] = [];
  payload.limit_status.forEach((row, index) => {
    if (index > 0) {
      const timeSeries = row[5];
      const hasTimeSeries =
        Array.isArray(timeSeries) && timeSeries.length > 2;

      returnData.push({
        number: index,
        name: row[0],
        value: row[3],
        limit: row[2],
        status: row[1] as Status,
        timeSeries: hasTimeSeries
          ? (timeSeries as ProspectusRestrictionsTimeSeries)
          : null,
      });
    }
  });

  return returnData;
}

interface IProspectusRestrictionsFirstLevelTableProps {
  activeFund: string;
  pageModel: ProspectusRestrictionsPageModel;
  isFetching?: boolean;
  activeFundName: string;
  fundDetails: Fund;
  section: string;
}

function FirstLevelTable({
  pageModel,
  activeFund,
  activeFundName,
  fundDetails,
  section,
  isFetching = false,
}: IProspectusRestrictionsFirstLevelTableProps): ReactElement {
  const payload = extractProspectusRestrictionsPayload(pageModel)!;
  const tableData = useBuildFirstLevelProspectusRestrictionsData(payload);
  const resolvedPositionDate =
    pageModel.selected_position_date ||
    pageModel.returned_position_date ||
    '';
  const positionDate = !isFetching ? resolvedPositionDate : 'no_date';

  return (
    <GridItem xs={12} card>
      <CustomTable<IProspectusRestrictionsFirstLevelData>
        selectedPositionDate={resolvedPositionDate}
        columns={firstLevelColumns(
          section,
          activeFund,
          activeFundName,
          positionDate,
        )}
        csvFields={csvProspectusRestrictionsFirstLevel}
        showToolbar={true}
        data={tableData}
        options={{
          exportButton: true,
          search: true,
          paging: false,
          raptorExport: true,
        }}
        toolbarComponents={{
          toolbarTitle: fundDetails?.name,
        }}
        onRowClick={(event: any, rowData, togglePanel: any) => {
          return togglePanel!();
        }}
        detailPanel={[
          {
            render: (rowData) => {
              return (
                <SecondLevelTable
                  selectedPositionDate={resolvedPositionDate}
                  rowData={rowData as IProspectusRestrictionsFirstLevelData}
                  report={payload}
                  fundDetails={fundDetails}
                  section={section}
                />
              );
            },
          },
        ]}
      />
    </GridItem>
  );
}

export default FirstLevelTable;
